import * as Dialog from "@radix-ui/react-dialog";
import { Trans, useTranslation } from "react-i18next";
import { useState, type FormEvent } from "react";

import { Switch } from "@/components/ui/switch";
import type {
  ConsentDecision,
  ConsentDecisions,
} from "@/engine/diagnostics/consent";
import type { ConsentChoice } from "@/engine/support/consentCookie";
import { useConsent } from "@/hooks/useConsent";

/**
 * The pre-connect consent surface: a blocking modal when the visitor's region
 * requires a choice and none is on record, a dismissible bottom panel otherwise.
 *
 * **Its buttons work before the application does.** The server draws it into the
 * first frame (#531), so it is pressed before the bundle has attached; the
 * buttons therefore submit a real form to `/api/consent`, and once the
 * application is live the submit is intercepted and saved in place.
 */

type SurfaceMode = "modal" | "panel";

interface ConsentPreferencesViewProps {
  mode: SurfaceMode;
  /** The decision each optional toggle starts on. */
  initial: ConsentDecisions;
  onSave: (decisions: ConsentDecisions) => void;
  /** Panel only — close without choosing. */
  onDismiss?: (() => void) | undefined;
}

const asDecision = (on: boolean): ConsentDecision =>
  on ? "granted" : "denied";

function CategoryRow({
  title,
  body,
  control,
}: {
  title: string;
  body: string;
  control: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
      <div className="shrink-0 pt-0.5">{control}</div>
    </div>
  );
}

export function ConsentPreferencesView({
  mode,
  initial,
  onSave,
}: ConsentPreferencesViewProps) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(initial.analytics === "granted");
  const [marketing, setMarketing] = useState(initial.marketing === "granted");

  // One handler for every button, reading which one was pressed: a click
  // handler that saved would unmount the form before the browser submitted it.
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submitter =
      "submitter" in event.nativeEvent ? event.nativeEvent.submitter : null;
    const choice =
      submitter instanceof HTMLButtonElement ? submitter.value : null;
    if (choice === "reject") {
      onSave({ analytics: "denied", marketing: "denied" });
    } else if (choice === "accept") {
      onSave({ analytics: "granted", marketing: "granted" });
    } else {
      onSave({
        analytics: asDecision(analytics),
        marketing: asDecision(marketing),
      });
    }
  };

  const secondary =
    "rounded-2xl border border-border/60 px-4 py-2 text-sm text-foreground hover:bg-muted/50";

  const body = (
    <form
      method="post"
      action="/api/consent"
      onSubmit={submit}
      className="space-y-4"
    >
      {/* What "Save choices" sends before the switches are live: the switches
          only exist as buttons until the application attaches to them. */}
      <input type="hidden" name="analytics" value={asDecision(analytics)} />
      <input type="hidden" name="marketing" value={asDecision(marketing)} />

      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">
          {t("consent.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          <Trans
            i18nKey="consent.description"
            components={[<span key="0" className="font-medium" />]}
          />
        </p>
      </div>

      <div className="divide-y divide-border/60">
        <CategoryRow
          title={t("consent.category.necessary.title")}
          body={t("consent.category.necessary.body")}
          control={
            <span className="text-xs font-medium text-muted-foreground">
              {t("consent.alwaysOn")}
            </span>
          }
        />
        <CategoryRow
          title={t("consent.category.analytics.title")}
          body={t("consent.category.analytics.body")}
          control={
            <Switch
              data-testid="consent-toggle-analytics"
              aria-label={t("consent.category.analytics.title")}
              checked={analytics}
              onCheckedChange={setAnalytics}
            />
          }
        />
        <CategoryRow
          title={t("consent.category.marketing.title")}
          body={t("consent.category.marketing.body")}
          control={
            <Switch
              data-testid="consent-toggle-marketing"
              aria-label={t("consent.category.marketing.title")}
              checked={marketing}
              onCheckedChange={setMarketing}
            />
          }
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <button
          type="submit"
          name="choice"
          value={"reject" satisfies ConsentChoice}
          data-testid="consent-reject-optional"
          className={secondary}
        >
          {t("consent.rejectOptional")}
        </button>
        <button
          type="submit"
          name="choice"
          value={"save" satisfies ConsentChoice}
          data-testid="consent-save"
          className={secondary}
        >
          {t("consent.save")}
        </button>
        <button
          type="submit"
          name="choice"
          value={"accept" satisfies ConsentChoice}
          data-testid="consent-accept-all"
          className="rounded-2xl border border-border/60 bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          {t("consent.acceptAll")}
        </button>
      </div>
    </form>
  );

  if (mode === "modal") {
    return (
      // Deliberately not inside `Dialog.Portal`: a portal renders nothing on the
      // server, and this surface has to be in the first frame (#531). Overlay
      // and content are both `fixed`, so they cover the viewport from here.
      <Dialog.Root open>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-overlay/50 backdrop-blur-sm" />
        <Dialog.Content
          data-testid="consent-modal"
          aria-label={t("consent.title")}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-border/60 bg-card p-5 shadow-lg"
        >
          <Dialog.Title className="sr-only">{t("consent.title")}</Dialog.Title>
          {body}
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <div
      role="region"
      aria-label={t("consent.reopen")}
      data-testid="consent-panel"
      className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-2xl rounded-t-3xl border border-border/60 bg-card/95 p-4 shadow-lg backdrop-blur-sm"
    >
      {body}
    </div>
  );
}

export function ConsentPreferences() {
  const consent = useConsent();
  if (!consent.showSurface) return null;
  return (
    <ConsentPreferencesView
      mode={consent.blocksConnect ? "modal" : "panel"}
      initial={consent.decisions}
      onSave={consent.save}
      onDismiss={consent.blocksConnect ? undefined : consent.dismiss}
    />
  );
}
